/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/Login.tsx
 * PURPOSE: SC-001-LOGIN — tenant user login (email + password), React port of the
 *          hi-fi reference, routed by server-resolved home_route
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * DATE: 2026-07-26
 * COMPLIANCE: SDLC 6.3.1 + Three-Tier Auth Architecture + Zero Mock Tolerance
 * PILLAR: P1 (Stage 04 - BUILD) + P2 (Sprint Governance) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Vietnamese F&B Workforce Management
 * - Impact: Security Critical — single entry point for all tenant users; no email
 *   enumeration, no field-specific error leakage (SC-001-LOGIN §A3/§A4)
 * - Stakeholders: All tenant users (TA / Manager / Employee); Platform Admins use
 *   a separate page (v0 scope excludes Platform Admin login from this screen)
 *
 * @technical-architecture
 * - Layer: Presentation Layer
 * - Pattern: CSS Modules (Login.module.css) on nqh-design-tokens.css; single
 *   role->route source (utils/roleDashboardMap.ts), server home_route preferred
 * - Dependencies: React 18, useAuthStore, POST /api/v1/tenant-users/login
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md (SC-001-LOGIN §2)
 * - Design: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-SCREEN-SPEC-MAP.md (SC-001-LOGIN §A/§B/§C)
 * - Hi-fi reference (ported, not re-designed): docs/02-design/04-ui-ux-design/design-imports/ui-ux-nqhbot/v0/schedule/shared/login.html
 * - Related: docs/03-integrate/01-API-Contracts/THREE-TIER-AUTH-API-CONTRACT.md
 * - Related: docs/02-design/18-adrs/06-decisions/DD-0002-three-tier-role-architecture.md
 * - Backend: backend/app/api/v1/tenant_users.py (POST /login), backend/app/services/tenant_user_service.py
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/auth";
import type { TenantLoginSingleResponse } from "../types/auth";
import apiWrapper from "../services/api/apiWrapper";
import { getDashboardRouteForRole } from "../utils/roleDashboardMap";
import styles from "./Login.module.css";

/**
 * Nhà cung cấp SSO hiển thị trên trang đăng nhập.
 *
 * Nguồn: biến build `VITE_SSO_PROVIDERS`, dạng "id:Nhãn,id:Nhãn".
 * KHÔNG hardcode: NQH là MỘT khách hàng có IdP. Khách hàng khác build cùng mã
 * nguồn này mà không đặt biến sẽ không thấy nút nào — đúng ý định.
 * Backend vẫn là nơi chặn thật (SSO_ENABLED_TENANTS + 404 nếu provider lạ);
 * biến này chỉ quyết định có VẼ nút hay không.
 */
const ssoProviders: Array<{ id: string; label: string }> = (
  import.meta.env.VITE_SSO_PROVIDERS ?? ""
)
  .split(",")
  .map((s: string) => s.trim())
  .filter(Boolean)
  .map((entry: string) => {
    const [id, ...rest] = entry.split(":");
    return { id: id.trim(), label: (rest.join(":") || id).trim() };
  })
  .filter((p: { id: string }) => p.id.length > 0);

type BannerKind = "error" | "offline";
interface BannerState {
  kind: BannerKind;
  message: string;
}

/** Heroicons outline "eye" (mật khẩu đang ẨN — bấm để hiện) — ported from
 *  design-imports/.../common.js SVG_EYE, no emoji per NQH-BRAND-DESIGN-SYSTEM. */
const EyeIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} width={22} height={22} aria-hidden="true" focusable="false">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

/** Heroicons outline "eye-off" (mật khẩu đang HIỆN — bấm để ẩn). */
const EyeOffIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} width={22} height={22} aria-hidden="true" focusable="false">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
  </svg>
);

/** Banner alert icon (warning triangle) — matches hi-fi login.html #banner svg. */
const AlertIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} width={20} height={20} aria-hidden="true" focusable="false">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
  </svg>
);

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const { setUser, setTokens, setTenantAuth } = useAuthStore();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [banner, setBanner] = useState<BannerState | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setBanner(null);

    // STATE ERROR — trường rỗng khi gửi → chặn (SC-001-LOGIN §A1/§A2)
    if (!email.trim() || !password) {
      setBanner({ kind: "error", message: "Vui lòng nhập đầy đủ thông tin" });
      return;
    }

    // STATE OFFLINE — mất mạng lúc gửi (SC-001-LOGIN §A5)
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      setBanner({ kind: "offline", message: "Lỗi kết nối. Thử lại" });
      return;
    }

    // STATE LOADING — spinner + disabled tới khi có phản hồi (SC-001-LOGIN §A6)
    setIsLoading(true);

    try {
      // BUG-010 fix: Use relative URL — nginx proxy handles /api → backend
      // Previous code bypassed proxy with hardcoded port 7200, causing CORS issues in E2E
      const response = await fetch("/api/v1/tenant-users/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 403) {
          // SC-001-LOGIN §A4 — account disabled (correct credentials, is_active=False)
          setBanner({
            kind: "error",
            message: data.detail || "Tài khoản đã bị vô hiệu hoá. Vui lòng liên hệ quản trị viên.",
          });
        } else {
          // SC-001-LOGIN §A3 — wrong credentials, generic (no field-level leak)
          setBanner({ kind: "error", message: data.detail || "Thông tin đăng nhập không đúng" });
        }
        return;
      }

      const singleData = data as TenantLoginSingleResponse;

      // Store tokens
      setTokens(singleData.access_token, singleData.refresh_token);

      // Sprint 55 S55-002: Read capabilities + role from response body
      // SC-001-LOGIN §C: home_route (server-resolved) persisted alongside — see
      // utils/roleDashboardMap.ts for the single role->route source.
      setTenantAuth(
        singleData.tenant_key,
        singleData.capabilities || [],
        singleData.role || singleData.user.role,
        singleData.home_route ?? null,
      );
      // S02-001 fix: propagate tenant key to apiWrapper for X-Tenant-Key header
      apiWrapper.setTenantKey(singleData.tenant_key);

      // Store user
      // Per DD-0002: store full user profile including branch_id for GPS clock-in
      // 2026-08-25: map `position` (vai trò/công việc) từ TenantUser.to_dict()
      // để EmployeeDashboard header render `{position} · {start}–{end}` theo
      // wireframe SC-019-EMP-HOME (verify SUMMARY mục 1b — position bị bỏ sót).
      setUser({
        id: singleData.user.id,
        email: singleData.user.email || email,
        full_name: singleData.user.full_name,
        role: singleData.user.role,
        is_active: singleData.user.is_active,
        branch_id: singleData.user.branch_id,
        organization_id: singleData.user.organization_id,
        // ISS-PILOT-12 (2026-08-09): login response ĐÃ có employee_code
        // (TenantUser.to_dict()) nhưng bị bỏ sót ở đây khi build user store —
        // /profile luôn hiện "—" dù backend trả đúng giá trị.
        employee_code: singleData.user.employee_code,
        position: singleData.user.position,
        // BOT-089 fix: propagate `must_change_password` so ChangePassword.tsx
        // can hide the "Quay lại" escape hatch when the user is FORCED to
        // change their password (after TA reset). Without this field the
        // guard `!user?.must_change_password` always evaluated true, letting
        // any user bypass the mandatory password change by clicking "Quay lại".
        must_change_password: singleData.user.must_change_password,
      });

      // BOT-005 fix: a temp password (TA "Đặt lại mật khẩu") must force the
      // change-password screen before any dashboard — this check must win over
      // home_route/role routing below, not just append to it.
      if (singleData.user.must_change_password) {
        navigate("/change-password");
        return;
      }

      // Navigate — prefer server-provided home_route (SC-001-LOGIN §C), fall back
      // to the shared role->route map for older backend responses.
      const dashboard = singleData.home_route || getDashboardRouteForRole(singleData.user.role);
      navigate(dashboard);
    } catch {
      // Network failure (fetch threw — DNS/CORS/backend down, distinct from the
      // pre-flight navigator.onLine check above, which only catches the browser
      // already knowing it's offline before the request is even sent).
      setBanner({ kind: "offline", message: "Lỗi kết nối. Thử lại" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className={styles.loginShell}>
      {/* Brand-panel (>=768px) — nội dung user-approved, xem NOTE trong Login.module.css */}
      <aside className={styles.brandPanel} aria-hidden="true">
        <div className={styles.brandLogo}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} width={30} height={30} aria-hidden="true" focusable="false">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 9h12v5.5A4.5 4.5 0 0111.5 19h-3A4.5 4.5 0 014 14.5V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M16 10h1.5a2.75 2.75 0 010 5.5H16M7.5 3.5c0 1.1-.9 1.3-.9 2.5M11.5 3.5c0 1.1-.9 1.3-.9 2.5" />
          </svg>
          <span>NQH-Bot Platform</span>
        </div>
        <div className={styles.brandHero}>
          <h1>Quản lý lịch làm việc &amp; chấm công cho chuỗi F&amp;B</h1>
          <p>Một nền tảng cho lên lịch, chấm công GPS, và vận hành đa chi nhánh — chuẩn hoá theo Nhật Quang Holding.</p>
          <div className={styles.brandFeats}>
            <div>
              <span className={styles.brandFeatIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} width={16} height={16} aria-hidden="true" focusable="false">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                </svg>
              </span>
              Lên lịch &amp; xuất bản ca theo chi nhánh
            </div>
            <div>
              <span className={styles.brandFeatIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} width={16} height={16} aria-hidden="true" focusable="false">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                </svg>
              </span>
              Chấm công GPS + ảnh xác thực
            </div>
            <div>
              <span className={styles.brandFeatIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} width={16} height={16} aria-hidden="true" focusable="false">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                </svg>
              </span>
              Phân quyền theo vai trò &amp; tổ chức
            </div>
          </div>
        </div>
        <div className={styles.brandFoot}>© 2026 Nhật Quang Holding · NQH-Bot Platform</div>
      </aside>

      <section className={styles.formPanel}>
        {/* Brand block mobile (<768px, 1 cột theo mockup) */}
        <div className={styles.brandMobile}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} width={40} height={40} aria-hidden="true" focusable="false">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 9h12v6a4 4 0 01-4 4H8a4 4 0 01-4-4V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M16 10h1.5a2.5 2.5 0 010 5H16" />
            <path strokeLinecap="round" d="M7.5 5.5c0-1 .9-1 .9-2M11.5 5.5c0-1 .9-1 .9-2" />
          </svg>
          <span className={styles.brandName}>NQH-Bot Platform</span>
        </div>

        <div className={styles.card}>
          <h1>Đăng nhập vào hệ thống</h1>

          {/* Banner lỗi — ẩn khi rỗng (STATE DEFAULT), hiện khi ERROR/OFFLINE */}
          {banner && (
            <div
              className={`${styles.banner} ${banner.kind === "offline" ? styles.bannerOffline : styles.bannerError}`}
              role="alert"
              aria-live="assertive"
            >
              <span className={styles.bannerIcon}>
                <AlertIcon />
              </span>
              <span>{banner.message}</span>
            </div>
          )}

          <form onSubmit={handleLogin} noValidate>
            <div className={styles.field}>
              <label htmlFor="email">Email</label>
              <input
                id="email"
                data-testid="login-email"
                name="email"
                type="email"
                className={styles.input}
                placeholder="email@company.com"
                required
                autoComplete="email"
                maxLength={254}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="password">Mật khẩu</label>
              <div className={styles.inputAffix}>
                <input
                  id="password"
                  data-testid="login-password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  className={styles.input}
                  required
                  autoComplete="current-password"
                  maxLength={128}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className={styles.btnIcon}
                  aria-label={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              data-testid="login-submit"
              className={`${styles.btn} ${styles.btnPrimary} ${styles.btnBlock}`}
              disabled={isLoading}
              aria-busy={isLoading}
            >
              {isLoading && <span className={styles.spinner} aria-hidden="true" />}
              <span>Đăng nhập</span>
            </button>
          </form>

          {/* DD-0090 — SSO là LỰA CHỌN THÊM, không thay thế mật khẩu.
              NQH là một khách hàng có IdP; khách hàng khác không có SSO vẫn dùng
              tài khoản do NQH-Bot tạo và quản lý. Nút chỉ hiện khi backend khai
              báo tenant này bật SSO (VITE_SSO_PROVIDERS rỗng => không render gì). */}
          {ssoProviders.length > 0 && (
            <div className={styles.ssoBlock}>
              <div className={styles.ssoDivider}>
                <span>hoặc đăng nhập bằng</span>
              </div>
              {ssoProviders.map((p) => (
                <a
                  key={p.id}
                  data-testid={`sso-login-${p.id}`}
                  className={`${styles.btn} ${styles.btnSso} ${styles.btnBlock}`}
                  href={`/api/v1/auth/sso/login?provider=${encodeURIComponent(p.id)}`}
                >
                  {p.label}
                </a>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default Login;
